/**
 * Empty State Component
 * 
 * Reusable component for displaying empty states with actionable CTAs.
 * Used across pages to guide users on what to do next.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick?: () => void;
    href?: string;
  };
  variant?: 'default' | 'compact';
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  variant = 'default',
  className,
}) => {
  const isCompact = variant === 'compact';

  return (
    <div 
      className={cn(
        "flex flex-col items-center justify-center text-center",
        isCompact ? "py-8 px-4" : "py-16 px-6",
        className
      )}
    >
      {/* Icon */}
      <div 
        className={cn(
          "mb-4 text-muted-foreground flex items-center justify-center",
          isCompact ? "w-12 h-12" : "w-16 h-16"
        )}
      >
        {icon}
      </div>

      {/* Title */}
      <h3 
        className={cn(
          "font-semibold text-foreground mb-2",
          isCompact ? "text-base" : "text-lg"
        )}
      >
        {title}
      </h3>

      {/* Description */}
      <p 
        className={cn(
          "text-muted-foreground mb-6 max-w-md",
          isCompact ? "text-xs" : "text-sm"
        )}
      >
        {description}
      </p>

      {/* Actions */}
      {(primaryAction || secondaryAction) && (
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {primaryAction && (
            <Button 
              onClick={primaryAction.onClick}
              className="min-w-[160px]"
              size={isCompact ? "sm" : "default"}
            >
              {primaryAction.icon && (
                <span className="mr-2">{primaryAction.icon}</span>
              )}
              {primaryAction.label}
            </Button>
          )}
          
          {secondaryAction && (
            secondaryAction.href ? (
              <a 
                href={secondaryAction.href}
                className={cn(
                  "text-brand-primary hover:underline font-medium transition-colors",
                  isCompact ? "text-xs" : "text-sm"
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {secondaryAction.label}
              </a>
            ) : secondaryAction.onClick ? (
              <button
                onClick={secondaryAction.onClick}
                className={cn(
                  "text-brand-primary hover:underline font-medium transition-colors",
                  isCompact ? "text-xs" : "text-sm"
                )}
              >
                {secondaryAction.label}
              </button>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Specialized Empty State for common scenarios
 */

// No Data variant
export const EmptyStateNoData: React.FC<Omit<EmptyStateProps, 'icon'>> = (props) => (
  <EmptyState
    icon={<i className="fa-solid fa-database text-4xl"></i>}
    {...props}
  />
);

// Upload variant
export const EmptyStateUpload: React.FC<Omit<EmptyStateProps, 'icon'>> = (props) => (
  <EmptyState
    icon={<i className="fa-solid fa-cloud-arrow-up text-4xl"></i>}
    {...props}
  />
);

// Success variant
export const EmptyStateSuccess: React.FC<Omit<EmptyStateProps, 'icon'>> = (props) => (
  <EmptyState
    icon={<i className="fa-solid fa-circle-check text-4xl text-green-600"></i>}
    {...props}
  />
);

// Search variant
export const EmptyStateSearch: React.FC<Omit<EmptyStateProps, 'icon'>> = (props) => (
  <EmptyState
    icon={<i className="fa-solid fa-magnifying-glass text-4xl"></i>}
    {...props}
  />
);
